import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { motion } from 'framer-motion';
import API_BASE_URL from '../config/api';
import { FiMaximize2 } from 'react-icons/fi';

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/manage_gallery.php`);
                const data = await response.json();
                if (data.status === 'success') {
                    setImages(data.data);
                }
            } catch (error) {
                console.error("Error fetching gallery:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchImages();
    }, []);

    return (
        <div className="bg-slate-50 min-h-screen">
            <Navbar />
            
            {/* Header */}
            <div className="pt-32 pb-20 bg-white relative overflow-hidden">
                {/* Brand Background Elements */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-[100px] -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-600 rounded-full filter blur-[100px] translate-x-1/2 translate-y-1/2"></div>
                </div>
                
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest mb-4"
                    >
                        Visual Journey
                    </motion.div>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-6xl font-black text-slate-900 mb-6 uppercase tracking-tight"
                    >
                        OUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">GALLERY</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-600 max-w-2xl mx-auto font-medium"
                    >
                        Explore the vibrant atmosphere and state-of-the-art facilities at our academy.
                    </motion.p>
                </div>
            </div>

            {/* Gallery Grid */}
            <div className="container mx-auto px-4 py-16">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                    </div>
                ) : (
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                        {images.map((img, index) => (
                            <motion.div
                                key={img.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.05 }}
                                className="relative group break-inside-avoid rounded-2xl overflow-hidden shadow-lg border border-slate-100 cursor-pointer"
                                onClick={() => setSelectedImage(img)}
                            >
                                <img 
                                    src={`${API_BASE_URL}/../${img.image_path}`} 
                                    alt={img.caption}
                                    className="w-full h-auto transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                    <div className="flex justify-between items-center">
                                        <p className="text-white font-bold text-sm transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                                            {img.caption || 'Academy View'}
                                        </p>
                                        <FiMaximize2 className="text-white text-xl transform scale-75 group-hover:scale-100 transition-transform duration-300" />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-[100] bg-slate-950/95 flex items-center justify-center p-4 backdrop-blur-md"
                    onClick={() => setSelectedImage(null)}
                >
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative max-w-5xl w-full"
                    >
                        <img 
                            src={`${API_BASE_URL}/../${selectedImage.image_path}`} 
                            alt={selectedImage.caption}
                            className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl ring-1 ring-white/10"
                        />
                        <div className="mt-6 text-center">
                            <p className="text-white text-xl font-bold uppercase tracking-wide">
                                {selectedImage.caption}
                            </p>
                        </div>
                        <button 
                            className="absolute -top-12 right-0 text-white text-4xl hover:text-orange-500 transition-colors"
                            onClick={() => setSelectedImage(null)}
                        >
                            &times;
                        </button>
                    </motion.div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Gallery;
